import { __decorate } from "tslib";
import { attr, css, nullableNumberConverter } from '@microsoft/fast-element';
import { SystemColors } from '@microsoft/fast-web-utilities';
import { CSSCustomPropertyBehavior, designSystemProperty, DesignSystemProvider, designSystemProvider, forcedColorsStylesheetBehavior, DesignSystemProviderTemplate as template, } from '@microsoft/fast-foundation';
import { parseColorHexRGB } from '@microsoft/fast-colors';
import { createColorPalette, neutralForegroundRest } from '../color';
import { DesignSystemDefaults } from '../fluent-design-system';
import { DesignSystemProviderStyles as styles } from './design-system-provider.styles';
const color = new CSSCustomPropertyBehavior('neutral-foreground-rest', neutralForegroundRest, (el) => el);
const backgroundStyles = css `
  :host {
    background-color: var(--background-color);
    color: ${color.var};
  }
`.withBehaviors(color, forcedColorsStylesheetBehavior(css `
      :host {
        background-color: ${SystemColors.ButtonFace};
        box-shadow: 0 0 0 1px ${SystemColors.CanvasText};
        color: ${SystemColors.ButtonText};
      }
    `));
/**
 * The Fluent DesignSystemProvider Element. Implements {@link @microsoft/fast-foundation#DesignSystemProvider},
 * {@link @microsoft/fast-foundation#DesignSystemProviderTemplate}
 *
 *
 * @public
 * @remarks
 * HTML Element: \<fluent-design-system-provider\>
 */
let FluentDesignSystemProvider = class FluentDesignSystemProvider extends DesignSystemProvider {
    constructor() {
        super(...arguments);
        /**
         * Used to instruct the FASTDesignSystemProvider
         * that it should not set the CSS
         * background-color and color properties
         *
         * @remarks
         * HTML boolean boolean attribute: no-paint
         */
        this.noPaint = false;
    }
    noPaintChanged() {
        if (!this.noPaint && this.backgroundColor !== void 0) {
            this.$fastController.addStyles(backgroundStyles);
        }
        else {
            this.$fastController.removeStyles(backgroundStyles);
        }
    }
    backgroundColorChanged() {
        // If background changes or is removed, we need to
        // re-evaluate whether we should have paint styles applied
        this.noPaintChanged();
    }
    neutralBaseColorChanged(oldValue, newValue) {
        const color = parseColorHexRGB(newValue);
        if (color) {
            this.neutralPalette = createColorPalette(color);
        }
    }
    accentBaseColorChanged(oldValue, newValue) {
        const color = parseColorHexRGB(newValue);
        if (color) {
            this.accentPalette = createColorPalette(color);
        }
    }
};
__decorate([
    attr({ attribute: 'no-paint', mode: 'boolean' })
], FluentDesignSystemProvider.prototype, "noPaint", void 0);
__decorate([
    designSystemProperty({
        attribute: 'background-color',
        default: DesignSystemDefaults.backgroundColor,
    })
], FluentDesignSystemProvider.prototype, "backgroundColor", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-base-color',
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralBaseColor,
    })
], FluentDesignSystemProvider.prototype, "neutralBaseColor", void 0);
__decorate([
    designSystemProperty({
        attribute: 'accent-base-color',
        cssCustomProperty: false,
        default: DesignSystemDefaults.accentBaseColor,
    })
], FluentDesignSystemProvider.prototype, "accentBaseColor", void 0);
__decorate([
    designSystemProperty({
        attribute: false,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralPalette,
    })
], FluentDesignSystemProvider.prototype, "neutralPalette", void 0);
__decorate([
    designSystemProperty({
        attribute: false,
        cssCustomProperty: false,
        default: DesignSystemDefaults.accentPalette,
    })
], FluentDesignSystemProvider.prototype, "accentPalette", void 0);
__decorate([
    designSystemProperty({
        default: DesignSystemDefaults.density,
        converter: nullableNumberConverter,
    })
], FluentDesignSystemProvider.prototype, "density", void 0);
__decorate([
    designSystemProperty({
        attribute: 'design-unit',
        converter: nullableNumberConverter,
        default: DesignSystemDefaults.designUnit,
    })
], FluentDesignSystemProvider.prototype, "designUnit", void 0);
__decorate([
    designSystemProperty({
        attribute: 'direction',
        cssCustomProperty: false,
        default: DesignSystemDefaults.direction,
    })
], FluentDesignSystemProvider.prototype, "direction", void 0);
__decorate([
    designSystemProperty({
        attribute: 'base-height-multiplier',
        default: DesignSystemDefaults.baseHeightMultiplier,
        converter: nullableNumberConverter,
    })
], FluentDesignSystemProvider.prototype, "baseHeightMultiplier", void 0);
__decorate([
    designSystemProperty({
        attribute: 'base-horizontal-spacing-multiplier',
        converter: nullableNumberConverter,
        default: DesignSystemDefaults.baseHorizontalSpacingMultiplier,
    })
], FluentDesignSystemProvider.prototype, "baseHorizontalSpacingMultiplier", void 0);
__decorate([
    designSystemProperty({
        attribute: 'corner-radius',
        converter: nullableNumberConverter,
        default: DesignSystemDefaults.cornerRadius,
    })
], FluentDesignSystemProvider.prototype, "cornerRadius", void 0);
__decorate([
    designSystemProperty({
        attribute: 'elevated-corner-radius',
        converter: nullableNumberConverter,
        default: DesignSystemDefaults.elevatedCornerRadius,
    })
], FluentDesignSystemProvider.prototype, "elevatedCornerRadius", void 0);
__decorate([
    designSystemProperty({
        attribute: 'outline-width',
        converter: nullableNumberConverter,
        default: DesignSystemDefaults.outlineWidth,
    })
], FluentDesignSystemProvider.prototype, "outlineWidth", void 0);
__decorate([
    designSystemProperty({
        attribute: 'focus-outline-width',
        converter: nullableNumberConverter,
        default: DesignSystemDefaults.focusOutlineWidth,
    })
], FluentDesignSystemProvider.prototype, "focusOutlineWidth", void 0);
__decorate([
    designSystemProperty({
        attribute: 'disabled-opacity',
        converter: nullableNumberConverter,
        default: DesignSystemDefaults.disabledOpacity,
    })
], FluentDesignSystemProvider.prototype, "disabledOpacity", void 0);
__decorate([
    designSystemProperty({
        attribute: 'type-ramp-minus-2-font-size',
        default: DesignSystemDefaults.typeRampMinus2FontSize,
    })
], FluentDesignSystemProvider.prototype, "typeRampMinus2FontSize", void 0);
__decorate([
    designSystemProperty({
        attribute: 'type-ramp-minus-2-line-height',
        default: DesignSystemDefaults.typeRampMinus2LineHeight,
    })
], FluentDesignSystemProvider.prototype, "typeRampMinus2LineHeight", void 0);
__decorate([
    designSystemProperty({
        attribute: 'type-ramp-minus-1-font-size',
        default: DesignSystemDefaults.typeRampMinus1FontSize,
    })
], FluentDesignSystemProvider.prototype, "typeRampMinus1FontSize", void 0);
__decorate([
    designSystemProperty({
        attribute: 'type-ramp-minus-1-line-height',
        default: DesignSystemDefaults.typeRampMinus1LineHeight,
    })
], FluentDesignSystemProvider.prototype, "typeRampMinus1LineHeight", void 0);
__decorate([
    designSystemProperty({
        attribute: 'type-ramp-base-font-size',
        default: DesignSystemDefaults.typeRampBaseFontSize,
    })
], FluentDesignSystemProvider.prototype, "typeRampBaseFontSize", void 0);
__decorate([
    designSystemProperty({
        attribute: 'type-ramp-base-line-height',
        default: DesignSystemDefaults.typeRampBaseLineHeight,
    })
], FluentDesignSystemProvider.prototype, "typeRampBaseLineHeight", void 0);
__decorate([
    designSystemProperty({
        attribute: 'type-ramp-plus-1-font-size',
        default: DesignSystemDefaults.typeRampPlus1FontSize,
    })
], FluentDesignSystemProvider.prototype, "typeRampPlus1FontSize", void 0);
__decorate([
    designSystemProperty({
        attribute: 'type-ramp-plus-1-line-height',
        default: DesignSystemDefaults.typeRampPlus1LineHeight,
    })
], FluentDesignSystemProvider.prototype, "typeRampPlus1LineHeight", void 0);
__decorate([
    designSystemProperty({
        attribute: 'type-ramp-plus-2-font-size',
        default: DesignSystemDefaults.typeRampPlus2FontSize,
    })
], FluentDesignSystemProvider.prototype, "typeRampPlus2FontSize", void 0);
__decorate([
    designSystemProperty({
        attribute: 'type-ramp-plus-2-line-height',
        default: DesignSystemDefaults.typeRampPlus2LineHeight,
    })
], FluentDesignSystemProvider.prototype, "typeRampPlus2LineHeight", void 0);
__decorate([
    designSystemProperty({
        attribute: 'type-ramp-plus-3-font-size',
        default: DesignSystemDefaults.typeRampPlus3FontSize,
    })
], FluentDesignSystemProvider.prototype, "typeRampPlus3FontSize", void 0);
__decorate([
    designSystemProperty({
        attribute: 'type-ramp-plus-3-line-height',
        default: DesignSystemDefaults.typeRampPlus3LineHeight,
    })
], FluentDesignSystemProvider.prototype, "typeRampPlus3LineHeight", void 0);
__decorate([
    designSystemProperty({
        attribute: 'type-ramp-plus-4-font-size',
        default: DesignSystemDefaults.typeRampPlus4FontSize,
    })
], FluentDesignSystemProvider.prototype, "typeRampPlus4FontSize", void 0);
__decorate([
    designSystemProperty({
        attribute: 'type-ramp-plus-4-line-height',
        default: DesignSystemDefaults.typeRampPlus4LineHeight,
    })
], FluentDesignSystemProvider.prototype, "typeRampPlus4LineHeight", void 0);
__decorate([
    designSystemProperty({
        attribute: 'type-ramp-plus-5-font-size',
        default: DesignSystemDefaults.typeRampPlus5FontSize,
    })
], FluentDesignSystemProvider.prototype, "typeRampPlus5FontSize", void 0);
__decorate([
    designSystemProperty({
        attribute: 'type-ramp-plus-5-line-height',
        default: DesignSystemDefaults.typeRampPlus5LineHeight,
    })
], FluentDesignSystemProvider.prototype, "typeRampPlus5LineHeight", void 0);
__decorate([
    designSystemProperty({
        attribute: 'type-ramp-plus-6-font-size',
        default: DesignSystemDefaults.typeRampPlus6FontSize,
    })
], FluentDesignSystemProvider.prototype, "typeRampPlus6FontSize", void 0);
__decorate([
    designSystemProperty({
        attribute: 'type-ramp-plus-6-line-height',
        default: DesignSystemDefaults.typeRampPlus6LineHeight,
    })
], FluentDesignSystemProvider.prototype, "typeRampPlus6LineHeight", void 0);
__decorate([
    designSystemProperty({
        attribute: 'accent-fill-rest-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.accentFillRestDelta,
    })
], FluentDesignSystemProvider.prototype, "accentFillRestDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'accent-fill-hover-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.accentFillHoverDelta,
    })
], FluentDesignSystemProvider.prototype, "accentFillHoverDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'accent-fill-active-delta',
        cssCustomProperty: false,
        converter: nullableNumberConverter,
        default: DesignSystemDefaults.accentFillActiveDelta,
    })
], FluentDesignSystemProvider.prototype, "accentFillActiveDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'accent-fill-focus-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.accentFillFocusDelta,
    })
], FluentDesignSystemProvider.prototype, "accentFillFocusDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'accent-fill-selected-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.accentFillSelectedDelta,
    })
], FluentDesignSystemProvider.prototype, "accentFillSelectedDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'accent-foreground-rest-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.accentForegroundRestDelta,
    })
], FluentDesignSystemProvider.prototype, "accentForegroundRestDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'accent-foreground-hover-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.accentForegroundHoverDelta,
    })
], FluentDesignSystemProvider.prototype, "accentForegroundHoverDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'accent-foreground-active-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.accentForegroundActiveDelta,
    })
], FluentDesignSystemProvider.prototype, "accentForegroundActiveDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'accent-foreground-focus-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.accentForegroundFocusDelta,
    })
], FluentDesignSystemProvider.prototype, "accentForegroundFocusDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-fill-rest-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralFillRestDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralFillRestDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-fill-hover-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralFillHoverDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralFillHoverDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-fill-active-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralFillActiveDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralFillActiveDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-fill-focus-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralFillFocusDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralFillFocusDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-fill-selected-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralFillSelectedDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralFillSelectedDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-fill-input-rest-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralFillInputRestDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralFillInputRestDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-fill-input-hover-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralFillInputHoverDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralFillInputHoverDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-fill-input-active-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralFillInputActiveDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralFillInputActiveDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-fill-input-focus-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralFillInputFocusDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralFillInputFocusDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-fill-input-selected-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralFillInputSelectedDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralFillInputSelectedDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-fill-stealth-rest-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralFillStealthRestDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralFillStealthRestDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-fill-stealth-hover-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralFillStealthHoverDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralFillStealthHoverDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-fill-stealth-active-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralFillStealthActiveDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralFillStealthActiveDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-fill-stealth-focus-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralFillStealthFocusDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralFillStealthFocusDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-fill-stealth-selected-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralFillStealthSelectedDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralFillStealthSelectedDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-fill-toggle-hover-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralFillToggleHoverDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralFillToggleHoverDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-fill-toggle-hover-active',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralFillToggleActiveDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralFillToggleActiveDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-fill-toggle-hover-focus',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralFillToggleFocusDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralFillToggleFocusDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'base-layer-luminance',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.baseLayerLuminance,
    })
], FluentDesignSystemProvider.prototype, "baseLayerLuminance", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-fill-card-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralFillCardDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralFillCardDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-foreground-hover-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralForegroundHoverDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralForegroundHoverDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-foreground-active-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralForegroundActiveDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralForegroundActiveDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-foreground-focus-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralForegroundFocusDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralForegroundFocusDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-divider-rest-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralDividerRestDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralDividerRestDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-outline-rest-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralOutlineRestDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralOutlineRestDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-outline-hover-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralOutlineHoverDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralOutlineHoverDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-outline-active-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralOutlineActiveDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralOutlineActiveDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-outline-focus-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralOutlineFocusDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralOutlineFocusDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-contrast-fill-rest-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralContrastFillRestDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralContrastFillRestDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-contrast-fill-hover-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralContrastFillHoverDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralContrastFillHoverDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-contrast-fill-active-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralContrastFillActiveDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralContrastFillActiveDelta", void 0);
__decorate([
    designSystemProperty({
        attribute: 'neutral-contrast-fill-focus-delta',
        converter: nullableNumberConverter,
        cssCustomProperty: false,
        default: DesignSystemDefaults.neutralContrastFillFocusDelta,
    })
], FluentDesignSystemProvider.prototype, "neutralContrastFillFocusDelta", void 0);
FluentDesignSystemProvider = __decorate([
    designSystemProvider({
        name: 'fluent-design-system-provider',
        template,
        styles,
        shadowOptions: {
            mode: 'closed',
        },
    })
], FluentDesignSystemProvider);
export { FluentDesignSystemProvider };
